import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  type FeatureFlag,
  useGetFeatureFlagsQuery,
  useGetSecureFeatureFlagsQuery,
} from '../redux/api/feature-flags.api';
import type { COIStore } from '../redux/store';
import { UI_FEATURE_FLAGS } from '@constants/feature-flags';
import { VITE_FF_USE_NEW_BASE_FORM } from '@utilities/env';

export interface UseFeatureFlagsResult {
  flags: FeatureFlag[];
  error: FetchBaseQueryError | SerializedError | undefined;
  isFeatureEnabled: (flagId: string) => boolean;
}

function isNotFoundError(error: FetchBaseQueryError | SerializedError | undefined): boolean {
  return Boolean(error && 'status' in error && error.status === 404);
}

/**
 * Custom hook to access feature flags.
 * Combines public and secure feature flags.
 * Provides a lookup function to check if a specific feature is enabled.
 */
export function useFeatureFlags(): UseFeatureFlagsResult {
  const token = useSelector((state: COIStore) => state.auth.token);
  const [isFeatureFlagRouteUnavailable, setIsFeatureFlagRouteUnavailable] = useState(false);
  const featureFlagQueryOptions = {
    pollingInterval: 60000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: isFeatureFlagRouteUnavailable,
  };

  // Anonymous flags
  const anonymousFlagsQuery = useGetFeatureFlagsQuery(undefined, featureFlagQueryOptions);
  // Secure flags are only fetched for authenticated users.
  const secureFlagsQuery = useGetSecureFeatureFlagsQuery(undefined, {
    ...featureFlagQueryOptions,
    skip: !token || isFeatureFlagRouteUnavailable,
  });

  const hasNotFoundError =
    isNotFoundError(anonymousFlagsQuery.error) || isNotFoundError(secureFlagsQuery.error);

  useEffect(() => {
    if (hasNotFoundError) setIsFeatureFlagRouteUnavailable(true);
  }, [hasNotFoundError]);

  const flags = useMemo(() => {
    const anonymousFlags = anonymousFlagsQuery.data?.data.flags ?? [];
    const secureFlags = secureFlagsQuery.data?.data.flags ?? [];

    return [...anonymousFlags, ...secureFlags];
  }, [anonymousFlagsQuery.data, secureFlagsQuery.data]);

  const flagsById = useMemo(() => {
    return flags.reduce<Record<string, boolean>>((acc, flag) => {
      acc[flag.id] = flag.isEnabled;
      return acc;
    }, {});
  }, [flags]);

  const localOverrides = useMemo(() => {
    const overrides: Record<string, boolean> = {};
    if (VITE_FF_USE_NEW_BASE_FORM !== undefined) {
      overrides[UI_FEATURE_FLAGS.USE_NEW_BASE_FORM] = VITE_FF_USE_NEW_BASE_FORM;
    }
    return overrides;
  }, []);

  const isFeatureEnabled = useCallback(
    (flagId: string) => {
      if (flagId in localOverrides) {
        return localOverrides[flagId];
      }
      return Boolean(flagsById[flagId]);
    },
    [flagsById, localOverrides]
  );

  return {
    flags,
    error: hasNotFoundError ? undefined : (anonymousFlagsQuery.error ?? secureFlagsQuery.error),
    isFeatureEnabled,
  };
}
